import { consola } from 'consola'
import { computed, watch } from 'vue'
import { useBasketState } from '../useBasketState'
import { useCheckoutFormStore } from '../useCheckoutFormStore'
import { useInstitutionStore } from '@entities/institution'
import { 
  calculateDeliveryCost, 
  getMinOrderAmount,
  type DeliveryZone, 
} from '~/features/map/@x/basket'

/**
 * ✅ ОТДЕЛЬНЫЙ COMPOSABLE для расчета доставки
 * 
 * Отвечает ТОЛЬКО за:
 * - Определение текущей зоны доставки
 * - Расчет стоимости доставки
 * - Минимальную сумму заказа
 * 
 * Использует единый basketState вместо множества stores
 */
export function useDeliveryCalculation() {
  const basketState = useBasketState()
  const checkoutForm = useCheckoutFormStore()
  const institution = useInstitutionStore()
  
  /**
   * Текущая зона доставки
   * ✅ Если зона не выбрана, используем первую зону по умолчанию
   */
  const currentZone = computed((): DeliveryZone | null => {
    const zoneId = basketState.deliveryZoneId || checkoutForm.OrderForm.delivery_zone_id
    const inst = institution.institution as any
    
    consola.debug('🔍 [useDeliveryCalculation] currentZone:', {
      zoneId,
      hasInstitution: !!inst,
      hasZones: !!inst?.delivery_zones,
      zonesCount: inst?.delivery_zones?.length || 0,
      zones: inst?.delivery_zones,
    })
    
    if (!inst?.delivery_zones || !Array.isArray(inst.delivery_zones)) {
      consola.warn('⚠️ [useDeliveryCalculation] Нет зон доставки!')
      return null
    }
    
    // Если зона выбрана - ищем её
    if (zoneId) {
      const zone = inst.delivery_zones.find(
        (z: any) => Number(z.id) === Number(zoneId),
      )
      if (zone) {
        consola.debug('✅ [useDeliveryCalculation] Найдена выбранная зона:', zone)
        return zone
      }
    }
    
    // ✅ Если зона не выбрана - используем первую зону по умолчанию
    // Это позволяет показать минимальную сумму до выбора адреса
    const defaultZone = inst.delivery_zones[0] || null
    consola.debug('📍 [useDeliveryCalculation] Используем первую зону по умолчанию:', defaultZone)
    return defaultZone
  })
  
  /**
   * Является ли заказ доставкой
   */
  const isDeliveryOrder = computed(() => {
    return checkoutForm.OrderForm.method_receipt === 'delivery'
  })
  
  /**
   * Стоимость доставки (вычисляется реактивно)
   */
  const cost = computed(() => {
    // Если промокод дает бесплатную доставку
    if (basketState.isFreeDelivery) {
      return 0
    }
    
    // Если не доставка
    if (!isDeliveryOrder.value) {
      return 0
    }
    
    // Вычисляем стоимость на основе зоны и суммы корзины
    return calculateDeliveryCost(
      currentZone.value,
      basketState.cartTotal,
      true,
    )
  })
  
  /**
   * Минимальная сумма заказа для доставки
   */
  const minOrderAmount = computed(() => {
    const zone = currentZone.value
    const minAmount = getMinOrderAmount(zone)
    
    consola.debug('💰 [useDeliveryCalculation] minOrderAmount:', {
      zone,
      minAmount,
      min_order_limit: zone?.min_order_limit,
    })
    
    return minAmount
  })
  
  /**
   * Правила доставки для текущей зоны
   */
  const deliveryRules = computed(() => {
    return currentZone.value?.rules || []
  })
  
  /**
   * Достигнута ли минимальная сумма заказа
   */
  const isMinOrderReached = computed(() => {
    if (!isDeliveryOrder.value) return true
    return basketState.cartTotal >= minOrderAmount.value
  })
  
  // ========================================
  // SIDE EFFECTS: Обновляем basketState
  // ========================================
  
  /**
   * Автоматически обновляем стоимость доставки в state при изменении
   */
  watch(cost, (newCost) => {
    basketState.setDeliveryCost(newCost)
  }, { immediate: true })
  
  /**
   * Автоматически обновляем минимальную сумму заказа
   */
  watch(minOrderAmount, (newMin) => {
    basketState.setMinOrderAmount(newMin)
  }, { immediate: true })
  
  /**
   * Синхронизируем delivery_zone_id между формой и state
   */
  watch(
    () => checkoutForm.OrderForm.delivery_zone_id,
    (zoneId) => {
      if (zoneId !== basketState.deliveryZoneId) {
        basketState.setDeliveryZoneId(zoneId)
      }
    },
    { immediate: true },
  )
  
  return {
    // Computed
    currentZone,
    isDeliveryOrder,
    cost,
    minOrderAmount,
    deliveryRules,
    isMinOrderReached,
  }
}


